import {
  Logger as RealtimeLogger,
  BaseLogger,
  LogLevel,
  type CompleteLogItem,
  VERSION,
} from '@doxyme/realtime';
import { CustomError } from '@doxyme/api-services';
import { getLogger } from '../../libs/logger';
import {
  type RealtimeIdentity,
  type ChatUserId,
  type RealtimeMember,
  RealtimeMemberStatus,
  type RealtimeMessage,
  type LegacyMessage,
} from './realtime.types';

/**
 * Log level to pass from the Realtime client to the frontend logger.
 * By default it is 'info', which would prevent us processing 'debug' logs.
 */
BaseLogger.setLevel(LogLevel.debug);

export const SERVICE_NAME = 'realtime';

/**
 * Here we have internal parameters for Dependency Injection [DO NOT USE]
 */
export const initRealtimeLogger = (_RealtimeLogger = RealtimeLogger): void => {
  const realtimeLogger = getLogger(SERVICE_NAME, {
    package: SERVICE_NAME,
    packageVersion: VERSION,
  });

  let logContext = {};
  const setContextFn = (context: object): void => {
    logContext = context;
  };

  const logFn = (
    message: string,
    logItem: CompleteLogItem,
    level = LogLevel.debug,
  ): void => {
    const { date, action, meta } = logItem;
    const attributes = { date, action, meta };
    const prefix = `[Realtime/${action ?? '?'}]`;
    realtimeLogger[level](prefix, message, logContext, attributes);
  };

  _RealtimeLogger.override({ log: logFn, setContext: setContextFn });
};

export const getChatUserIdFromIdentity = <
  T extends RealtimeIdentity | undefined,
>(
  identity: T,
): ChatUserId<T> => (identity?.userId ?? identity?.id) as ChatUserId<T>;

export const getUniqueMembersByUserId = (
  members: Record<string, RealtimeMember>,
): Record<string, RealtimeMember> => {
  const entries = Object.entries(members);
  const uniqueMembers: Record<string, RealtimeMember> = {};

  for (const [id, user] of entries) {
    if (
      !Object.values(uniqueMembers).some(
        (member) =>
          member.identity.userId !== undefined &&
          member.identity.userId === user.identity.userId,
      )
    ) {
      uniqueMembers[id] = user;
    }
  }

  return uniqueMembers;
};

/**
 * Get member status by considering all existing members for the userId,
 * returns busy if at least one is busy, otherwise returns provided member status
 * @param membersMap - members map
 * @param member - target member, for which the status is determined
 * @returns {RealtimeMemberStatus | undefined} - status or undefined
 */
export const getMemberStatus = (
  membersMap: Record<string, RealtimeMember>,
  member?: RealtimeMember,
): RealtimeMemberStatus | undefined => {
  if (member === undefined || member.identity.userId === undefined) {
    return member?.state.status;
  }

  const membersByUserId = Object.values(membersMap).filter(
    (m) => m.identity.userId === member.identity.userId,
  );

  if (
    membersByUserId.some((m) => m.state.status === RealtimeMemberStatus.Busy)
  ) {
    return RealtimeMemberStatus.Busy;
  }

  return member.state.status;
};

export const throwNoMemberError = (
  id: string,
  message: RealtimeMessage | LegacyMessage,
  method: string,
): never => {
  const messageType =
    'messageType' in message ? message.messageType : 'unknown';
  const errorMessage = `Member with realtimeId: ${id} -- not found, messageType: ${messageType}`;
  const error = new CustomError(errorMessage);
  getLogger('Realtime').error(errorMessage, {
    method,
    error,
    messageType,
    id,
  });
  throw error;
};
